import { useState } from 'react';
import { FormGroup, Typography, FormControlLabel, Checkbox, Stack, Button } from '@mui/material';
import SimCardDownloadSharpIcon from '@mui/icons-material/SimCardDownloadSharp';
import { getTimesheetPDFByLocation } from '../../Services/ProcessedTimesheetService';
import ButtonWithDropdownMenu from '../../../Shared/Components/ButtonWithDropdownMenu';

const DownloadTimesheetsButton = ({
    entries = [],
    weekEndingDate,
    timesheetDetailsData,
    clinicianId,
    rawTimesheetId,
    clinicianName,
}) => {
    const [locationsToDownload, setLocationsToDownload] = useState([]);
    const [openDownloadModal, setOpenDownloadModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const entriesByLocation = timesheetDetailsData?.timeEntries?.entriesByLocation || [];

    const getPDFByLocation = async (locationId, locationName) => {
        try {
            setIsLoading(true);
            await getTimesheetPDFByLocation({
                clinicianId,
                rawTimesheetId,
                locationId,
                locationName,
                clinicianName,
                weekEndingDate,
            });
            setOpenDownloadModal(false);
            setLocationsToDownload([]);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenDownloadModal = () => {
        if (entriesByLocation.length === 1) {
            getPDFByLocation(entriesByLocation[0]?.locationId, entriesByLocation[0]?.locationName);
        } else {
            setOpenDownloadModal(true);
        }
    };

    const handleDownloadModalClose = () => setOpenDownloadModal(false);

    const downloadSelectedLocationsPDF = () => {
        if (locationsToDownload?.length > 0) {
            locationsToDownload.forEach((locationId) => {
                const location = entriesByLocation?.find((loc) => loc.locationId === locationId);
                if (location) {
                    getPDFByLocation(location.locationId, location.locationName);
                }
            });
        }
    };

    const handleCheckboxChange = (locationId) => (event) => {
        setLocationsToDownload((prev) =>
            event.target.checked ? [...prev, locationId] : prev.filter((id) => id !== locationId)
        );
    };

    const renderItemsToDownloadList = () => (
        <FormGroup
            sx={{
                width: '417px',
                padding: '0 24px',
                paddingTop: '8px',
                paddingBottom: '8px',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                boxSizing: 'border-box',
            }}
        >
            <Typography variant="h5">Approved timesheets</Typography>
            {entries?.map((entry, index) => (
                <FormControlLabel
                    key={entry.locationId ?? index}
                    control={
                        <Checkbox
                            checked={locationsToDownload.includes(entry.locationId)}
                            onChange={handleCheckboxChange(entry.locationId)}
                            sx={{
                                width: 18,
                                height: 18,
                                padding: 0,
                                alignSelf: 'flex-start',
                            }}
                            data-testid={`checkbox-${entry.locationName}`}
                        />
                    }
                    label={entry.locationName}
                    sx={{
                        alignItems: 'flex-start',
                        gap: '11px',
                        margin: 0,
                    }}
                />
            ))}
            <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
                <Button
                    onClick={() => setLocationsToDownload([])}
                    color="secondary"
                    variant="contained"
                    data-testid="clear-menu-button"
                >
                    Clear
                </Button>

                <Button
                    onClick={downloadSelectedLocationsPDF}
                    variant="contained"
                    data-testid="download-menu-button"
                    loading={isLoading}
                >
                    <Typography sx={{ fontSize: 12 }}>Download</Typography>
                </Button>
            </Stack>
        </FormGroup>
    );

    return entriesByLocation.length === 1 ? (
        <Button
            color="primary"
            variant="contained"
            sx={{
                fontSize: 12,
                gap: 0.25,
            }}
            onClick={handleOpenDownloadModal}
            loading={isLoading}
            startIcon={<SimCardDownloadSharpIcon sx={{ fontSize: 20 }} />}
        >
            Download Timesheet
        </Button>
    ) : (
        <ButtonWithDropdownMenu
            label="Download Timesheet"
            buttonProps={{
                variant: 'contained',
                sx: { fontSize: 12, gap: 0.25 },
                color: 'primary',
                startIcon: <SimCardDownloadSharpIcon sx={{ fontSize: 20 }} />,
            }}
            menuProps={{
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            }}
            dropdownContent={renderItemsToDownloadList()}
            open={openDownloadModal}
            onOpen={handleOpenDownloadModal}
            onClose={handleDownloadModalClose}
            hideChevron={entriesByLocation.length === 1}
        />
    );
};

export default DownloadTimesheetsButton;
